import { BLACK_TEXT, WHITE } from 'constants/styling/theme'

import labelAiBlack from 'assets/img/ai_label/LABEL_AI_black.svg'
import labelAiModifiedBlack from 'assets/img/ai_label/LABEL_AI_MODIFIED_black.svg'
import labelAiModifiedWhite from 'assets/img/ai_label/LABEL_AI_MODIFIED_white.svg'
import labelAiWhite from 'assets/img/ai_label/LABEL_AI_white.svg'

/** The label variants a client can place on an AI-staged image (EU AI Act Art. 50(4)). */
export enum AiLabelType {
  AI = 'AI',
  AI_MODIFIED = 'AI_MODIFIED',
  TEXT = 'TEXT',
}

/** Badge colour variants (matches the AI_LABEL_ASSETS keys). */
export type AiLabelColour = 'black' | 'white'

/** Opacity slider bounds, in percent (epic ISSUE-2734 design spec; 50% default per QA round 2, ISSUE-2839). */
export const AI_LABEL_OPACITY_MIN = 50
export const AI_LABEL_OPACITY_MAX = 100
export const AI_LABEL_OPACITY_STEP = 5
export const AI_LABEL_OPACITY_DEFAULT = 50

/** Hex values the label colours render with (overlay CSS and canvas draw), from the theme palette. */
export const AI_LABEL_COLOUR_HEX: Record<AiLabelColour, string> = {
  black: BLACK_TEXT,
  white: WHITE,
}

/**
 * Soft text-shadow per colour variant — thin type needs it for legibility on
 * busy photos in a way a filled badge does not (epic design spec). Contrast is
 * inverted: light halo under black type, dark halo under white type.
 */
export const AI_LABEL_TEXT_SHADOW: Record<AiLabelColour, string> = {
  black: 'rgba(255, 255, 255, 0.35)',
  white: 'rgba(0, 0, 0, 0.35)',
}

/** Normalised label centre: fractions of image width/height. */
export interface AiLabelPosition {
  x: number
  y: number
}

/** Size ranges as fractions of image width (epic ISSUE-2734 design spec, hard-clamped). */
export const AI_LABEL_SIZE_RANGE: Record<AiLabelType, { min: number, max: number }> = {
  [AiLabelType.AI]: { min: 0.03, max: 0.25 },
  [AiLabelType.AI_MODIFIED]: { min: 0.10, max: 0.50 },
  [AiLabelType.TEXT]: { min: 0.01, max: 0.05 },
}

/** Keyboard steps, as fractions of the relevant image dimension. */
export const AI_LABEL_MOVE_STEP = 0.01
export const AI_LABEL_MOVE_STEP_LARGE = 0.05
export const AI_LABEL_RESIZE_STEP: Record<'badge' | 'text', { small: number, large: number }> = {
  badge: { small: 0.01, large: 0.05 },
  text: { small: 0.001, large: 0.005 },
}

/** Snap distance for the drag guides, as a fraction of the relevant image dimension. */
export const AI_LABEL_SNAP_FRACTION = 0.02

/**
 * AI label state (EU AI Act Art. 50(4)).
 * Colour and opacity apply to all three label types.
 */
export interface AiLabelState {
  showAiLabel: boolean
  labelType: AiLabelType
  labelText: string
  colour: AiLabelColour
  /** Percent, AI_LABEL_OPACITY_MIN..AI_LABEL_OPACITY_MAX */
  opacity: number
  /** Normalised label centre; null = default bottom-right placement */
  position: AiLabelPosition | null
  /** Badge width / text font size as a fraction of image width; null = type default */
  sizeFraction: number | null
}

/** Aspect ratios (width / height) of the trimmed label SVGs. */
export const AI_LABEL_ASPECT: Record<AiLabelType.AI | AiLabelType.AI_MODIFIED, number> = {
  [AiLabelType.AI]: 1,
  [AiLabelType.AI_MODIFIED]: 4.619,
}

/** Default badge widths as a fraction of the image width (epic spec × 0.65 — 35% smaller per QA round 2, ISSUE-2839). */
export const AI_LABEL_WIDTH_FRACTION: Record<AiLabelType.AI | AiLabelType.AI_MODIFIED, number> = {
  [AiLabelType.AI]: 0.039,
  [AiLabelType.AI_MODIFIED]: 0.117,
}

/** Default text label font size as a fraction of the image width (× 0.65, see AI_LABEL_WIDTH_FRACTION). */
export const AI_LABEL_TEXT_FONT_FRACTION = 0.0156

/** Inset from the image edges as a fraction of the image's shorter side. */
export const AI_LABEL_INSET_FRACTION = 0.03

/** Black variants are the ISSUE-2761 default; white variants ship for ISSUE-2762's colour control. */
export const AI_LABEL_ASSETS: Record<AiLabelType.AI | AiLabelType.AI_MODIFIED, { black: string, white: string }> = {
  [AiLabelType.AI]: { black: labelAiBlack, white: labelAiWhite },
  [AiLabelType.AI_MODIFIED]: { black: labelAiModifiedBlack, white: labelAiModifiedWhite },
}
